import type {Directive} from 'vue';
import type {RouteLocationNormalized} from 'vue-router';
import type {TrackingAction, TrackingEntity} from '~/composables/useTracking';

// Click tracking (TAL-11043), the queue is composables/useTracking.ts.
//
// v-track only writes data attributes. One capture-phase click listener on the document reads the closest
// tagged element, so tracking something new is one attribute and costs no listener of its own. Capture,
// because several controls stop their clicks from bubbling (bookmarks, the connect icon). Capture also sees
// clicks a component goes on to cancel, so a tag belongs only where the click always does what it says: a
// carousel whose clones lose their listeners, or whose drags end in a click, tracks from its handler instead
// (components/event-blocks/sponsorsBlock.vue).
//
//   v-track="{entity: 'session', id: session.id}"          an open, the default action
//   v-track="{entity: 'filter', action: 'apply', item: 'featured'}"
//   v-track="{entity: 'notification', id, event: eventId}"  belongs to another event than the one on screen
//   v-track="false"                                        a control inside a tagged card (a bookmark) that is
//                                                          not the card's open
//   v-track="null"                                         not tracked
export type TrackBinding = {
  entity: TrackingEntity,
  action?: TrackingAction,
  id?: number | null,
  item?: string | null,
  event?: number | null,
} | false | null | undefined;

const ATTRIBUTES = ['track', 'trackEntity', 'trackAction', 'trackId', 'trackItem', 'trackEvent'] as const;

const applyBinding = (el: HTMLElement, binding: TrackBinding) => {
  for (const attribute of ATTRIBUTES) {
    delete el.dataset[attribute];
  }
  if (binding === false) {
    el.dataset.track = 'off';
    return;
  }
  if (!binding) {
    return;
  }
  el.dataset.trackEntity = binding.entity;
  el.dataset.trackAction = binding.action ?? 'open';
  if (binding.id != null) el.dataset.trackId = String(binding.id);
  if (binding.item) el.dataset.trackItem = binding.item;
  // null is kept as an empty attribute: the click belongs to no event and is not tracked.
  if (binding.event !== undefined) el.dataset.trackEvent = binding.event == null ? '' : String(binding.event);
};

const trackDirective: Directive<HTMLElement, TrackBinding> = {
  mounted: (el, {value}) => applyBinding(el, value),
  updated: (el, {value}) => applyBinding(el, value),
  // Attributes are added on the client only, a server-rendered page needs none.
  getSSRProps: () => ({}),
};

const toNumber = (value: string | undefined): number | null => value ? Number(value) : null;

export default defineNuxtPlugin((nuxtApp) => {
  nuxtApp.vueApp.directive('track', trackDirective);
  if (import.meta.server) {
    return;
  }

  const router = useRouter();
  const {currentUserId} = useWadAuth();
  const {$api} = useNuxtApp();

  configureTracking({
    userId: () => currentUserId.value ?? null,
    // Only the route's event. A global page (the event list, notifications) belongs to no event, even when
    // one is still loaded from before: a click there is tracked only if it names its own (a notification, the
    // menu's links into the loaded event).
    eventId: () => Number(router.currentRoute.value.params.eventId) || null,
    screen: () => {
      const name = router.currentRoute.value.name;
      return typeof name === 'string' ? name : null;
    },
    send: (eventId, body) => $api.v2.postAppTracking(eventId, body),
    appVersion: useRuntimeConfig().public.bugsnagAppVersion as string | undefined,
  });

  document.addEventListener('click', (event) => {
    const el = (event.target as Element | null)?.closest?.<HTMLElement>('[data-track-entity],[data-track="off"]');
    if (!el || el.dataset.track === 'off') {
      return;
    }
    const eventId = el.dataset.trackEvent;
    trackClick(el.dataset.trackEntity as TrackingEntity, el.dataset.trackAction as TrackingAction, {
      entityId: toNumber(el.dataset.trackId),
      item: el.dataset.trackItem,
      ...(eventId === undefined ? {} : {eventId: toNumber(eventId)}),
    });
  }, {capture: true});

  // A screen view is the route name and nothing else: the params tell two sessions apart for the dedupe
  // but are not sent, and a change of query only (filters, a search term) is not a new screen.
  let lastScreen: string | null = null;
  const trackScreen = (route: RouteLocationNormalized) => {
    if (typeof route.name !== 'string') {
      return;
    }
    const key = `${route.name}${JSON.stringify(route.params)}`;
    if (key === lastScreen) {
      return;
    }
    lastScreen = key;
    trackClick('screen', 'view');
  };
  router.afterEach((to, _from, failure) => {
    if (!failure) {
      trackScreen(to);
    }
  });
  // The first screen may already be resolved before this plugin runs.
  void router.isReady().then(() => trackScreen(router.currentRoute.value));

  // Hidden is the last moment a phone reliably gives a page: after it the tab can be frozen or killed.
  // pagehide covers browsers that skip visibilitychange on unload. The send uses keepalive, so it outlives
  // the page (sendBeacon cannot carry the Authorization header).
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      void flushTracking();
    }
  });
  window.addEventListener('pagehide', () => void flushTracking());

  watch(currentUserId, (userId, previous) => {
    if (previous && userId !== previous) {
      resetTracking();
    }
  });
});
